import { Fragment, useEffect, useRef, useState } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import { useMutation } from 'react-query'
import ReactTooltip from 'react-tooltip'
import unique from 'just-unique'
import InfiniteScroll from 'react-infinite-scroll-component'
import {
  clearAnnouncementNotificationId,
  setUnreadAnnouncementCount,
  toggleStudentAnnouncementsSidebar
} from 'actions/announcements'
import withApiData from 'components/HOC/withApiData'
import FlexAlignCenter from 'components/styled/FlexAlignCenter'
import { Caption1, H5 } from 'components/styled/typography/typography'
import LoadingSpinner from 'components/mini/LoadingSpinner'
import {
  AnnouncementsSidebarContainer,
  AnnouncementsSidebarHeader,
  FilterButton,
  AnnouncementList,
  NotificationAnnouncementWrapper,
  MarkAll
} from './styles'
import useFetchData from './hooks/useFetchData'
import { clearAnnouncements, fetchAnnouncement, markAnnouncementRead } from './API'
import Announcement from './Announcement'
import Loading from './Loading'
import { useSwipeable } from 'react-swipeable'
import FilterEnabledSvg from '../../../static/images/icons/icon_filter_enabled.svg'
import FilterDisabledSvg from '../../../static/images/icons/icon_filter_disabled.svg'
import useIsGuardian from '../../../hooks/useIsGuardian'
import { postToLoggingService } from '../../../actions/logging'

const isOutsideClick = (event, element) => !element?.contains(event?.target)

const Index = ({ apiData, displayStudentSidebar, isSideNavCollapsed}) => {
  const dispatch = useDispatch()
  const container = useRef(null)
  const userId = useSelector(state => state.auth.user.id)
  const isGuardian = useIsGuardian()
  const shouldDisplay = useSelector(
    state => state.announcements.displayStudentSidebar
  )
  const notificationIdToFetch = useSelector(
    state => state.announcements.notificationId
  )
  const [notificationAnnouncement, setNotificationAnnouncement] = useState(null)
  const [announcements, setAnnouncements] = useState([])
  const [filterByUnread, setFilterByUnread] = useState(false)
  const [linkHeaders, setLinkHeaders] = useState(null)
  const [nextPage, setNextPage] = useState(1)

  const { isFetching, refetch } = useFetchData(apiData, {
    shouldDisplay,
    nextPage,
    filterByUnread,
    onSuccess: (data, linkHeaders) => {
      setAnnouncements(as => unique([...as, ...data]))
      setLinkHeaders(linkHeaders)
    }
  })

  const onAnnouncementReadToggled = res => {
    setAnnouncements(as =>
      as.map(a => {
        if (a.user_master_course_notification.id === res.id) {
          return {
            ...a,
            ...{
              user_master_course_notification: {
                ...a.user_master_course_notification,
                marked_read_at: res.marked_read_at
              }
            }
          }
        } else {
          return {
            ...a
          }
        }
      })
    )

    if (
      notificationAnnouncement &&
      notificationAnnouncement.user_master_course_notification.id === res.id
    ) {
      setNotificationAnnouncement(na => {
        return {
          ...na,
          ...{
            user_master_course_notification: {
              ...na.user_master_course_notification,
              marked_read_at: res.marked_read_at
            }
          }
        }
      })
    }
  }

  useEffect(() => {
    const fetchSpecificNotification = async () => {
      dispatch(clearAnnouncementNotificationId())
      const response = await fetchAnnouncement(apiData, notificationIdToFetch)
      setNotificationAnnouncement(response)
    }

    if (notificationIdToFetch) {
      fetchSpecificNotification()
    }
  }, [notificationIdToFetch])

  const pusherCallback = async ({
    user_ids,
    new_user_master_course_notification,
    toggled_user_master_course_notification
  }) => {
    if (
      user_ids.includes(userId) &&
      new_user_master_course_notification?.source_type ===
        'UserCourseSectionAnnouncement'
    ) {
      const { data } = await refetch()
      setAnnouncements(data)
      dispatch(setUnreadAnnouncementCount(1))
    }

    if (
      user_ids.includes(userId) &&
      toggled_user_master_course_notification?.source_type ===
        'UserCourseSectionAnnouncement' &&
      !toggled_user_master_course_notification?.marked_read_at
    ) {
      setAnnouncements(as =>
        as.map(a => {
          if (
            a.user_master_course_notification.id ===
            toggled_user_master_course_notification.id
          ) {
            return {
              ...a,
              ...{
                course_section_announcement: {
                  ...a.course_section_announcement,
                  text: toggled_user_master_course_notification.text
                },
                user_master_course_notification: {
                  ...a.user_master_course_notification,
                  marked_read_at:
                    toggled_user_master_course_notification.marked_read_at
                }
              }
            }
          } else {
            return {
              ...a
            }
          }
        })
      )

      dispatch(setUnreadAnnouncementCount(1))
    }

    return () => {
      document.body.classList.remove('no-scroll')
    }
  }

  useEffect(() => {
    let orgUsersChannel = window.oeQwPusherInstance.subscribe(
      `quest.organizations.${apiData.orgId}.users`
    )

    orgUsersChannel.bind(
      'student_master_course_notifications_updated',
      pusherCallback
    )

    return () => {
      orgUsersChannel.unbind(
        'student_master_course_notifications_updated',
        pusherCallback
      )
    }
  }, [])

  useEffect(() => {
    if (shouldDisplay) {
      dispatch(setUnreadAnnouncementCount(0))
      document.body.classList.add('no-scroll')
      window.removeEventListener('click', handleClickAway) // in case listener exists
      window.addEventListener('click', handleClickAway)
    } else {
      document.body.classList.remove('no-scroll')
      window.removeEventListener('click', handleClickAway)
    }

    resetState()
    setFilterByUnread(false)
  }, [shouldDisplay])

  const handleClickAway = e => {
    if (shouldDisplay && isOutsideClick(e, container?.current)) {
      dispatch(toggleStudentAnnouncementsSidebar(false))
    }
  }

  const resetState = () => {
    setNotificationAnnouncement(null)
    setAnnouncements([])
    setLinkHeaders([])
    setNextPage(1)
  }

  const filteredAnnouncements = announcements
    .filter(a =>
      filterByUnread ? !a.user_master_course_notification.marked_read_at : true
    )
    .filter(a =>
      !!notificationAnnouncement ? a.id !== notificationAnnouncement.id : true
    )

  const handlers = useSwipeable({
    onSwipedLeft: (eventData) => dispatch(
      toggleStudentAnnouncementsSidebar(!displayStudentSidebar)
    )
  });

  const toggleRead = async announcement => {
    const response = await markAnnouncementRead(
      apiData,
      announcement.master_course.id,
      announcement.user_master_course_notification.id
    )
    if (!!response) {
      onAnnouncementReadToggled(response)

      if (response.marked_read_at) {
        dispatch(postToLoggingService(
          'student_clear_notification',
          {
            action: 'announcement_marked_read',
            notification: {
              id: announcement.user_master_course_notification.id,
              type: 'section_announcement'
            },
            master_course: {
              id: announcement.master_course.id,
              name: announcement.master_course.name
            }
          }
        ))
      }
    }
  }

  const { mutate: markAllRead, isLoading: isClearingNotifications } = useMutation({
    mutationFn: () => clearAnnouncements(apiData),
    onSuccess: () => {
      const updater = an => ({
        ...an,
        user_master_course_notification: {
          ...an.user_master_course_notification,
          marked_read_at: an.marked_read_at ?? Date.now()
        }
      })

      setAnnouncements(ans => ans.map(updater))

      if (notificationAnnouncement) {
        setNotificationAnnouncement(updater)
      }

      dispatch(postToLoggingService(
        'student_clear_notification',
        { action: 'clear_all_announcements' }
      ))
    }
  })

  const hasData = filteredAnnouncements.length > 0 || !!notificationAnnouncement
  const isLoading = (!hasData && isFetching) || isClearingNotifications

  return (
    <AnnouncementsSidebarContainer
      ref={container}
      isSideNavCollapsed={isSideNavCollapsed}
      shouldDisplay={shouldDisplay}
      onClick={e => {
        e.stopPropagation()
      }}
      {...handlers}
    >
      <Fragment>
        <AnnouncementsSidebarHeader>
          <H5>Announcements</H5>
          <FlexAlignCenter spaced>
            {!isGuardian && announcements.length > 0 && (
              <MarkAll onClick={markAllRead}>
                Mark all as read
              </MarkAll>
            )}
            <FilterButton
              onClick={() => {
                resetState()
                setFilterByUnread(f => !f)
              }}
              enabled={filterByUnread}
              data-tip={filterByUnread ? 'View all' : 'Filter by unread'}
              data-for="announcements-filter-tooltip"
            >
              <img
                src={filterByUnread ? FilterEnabledSvg : FilterDisabledSvg}
                alt={filterByUnread ? 'View all' : 'Filter by unread'}
              />
            </FilterButton>
            <ReactTooltip
                id="announcements-filter-tooltip"
                place="right"
                effect="solid"
              />
          </FlexAlignCenter>
        </AnnouncementsSidebarHeader>
        {isLoading ? (
          <div style={{ marginTop: '100px' }}>
            <LoadingSpinner isGray isFullScreen={false} />
          </div>
        ) : hasData ? (
          <AnnouncementList id="announcements-notifications">
            {!!notificationAnnouncement && (
              <NotificationAnnouncementWrapper>
                <Announcement
                  announcement={notificationAnnouncement}
                  toggleRead={toggleRead}
                  showcased
                />
              </NotificationAnnouncementWrapper>
            )}
            <InfiniteScroll
              dataLength={filteredAnnouncements.length}
              next={() => {
                setNextPage(nextPage + 1)
              }}
              hasMore={!!linkHeaders && linkHeaders.next}
              loader={<Loading />}
              scrollableTarget="announcements-notifications"
            >
              {filteredAnnouncements.map((announcement, index) => (
                <Announcement
                  key={`announcement-${index}-${announcement.id}`}
                  s
                  announcement={announcement}
                  toggleRead={toggleRead}
                />
              ))}
            </InfiniteScroll>
          </AnnouncementList>
        ) : (
          <FlexAlignCenter
            style={{ justifyContent: 'center', marginTop: '36px' }}
          >
            <Caption1 fiveam>
              {filterByUnread
                ? 'No unread notifications.'
                : 'No announcements yet.'}
            </Caption1>
          </FlexAlignCenter>
        )}
      </Fragment>
    </AnnouncementsSidebarContainer>
  )
}

export const mapStateToProps = state => ({
  displayStudentSidebar: state.announcements.displayStudentSidebar,
})

export default withApiData(connect(mapStateToProps)(Index))
